import React, { useEffect, useRef, useCallback } from 'react'

const ChatMessageList = ({ messages, currentUser, loading, onMessagesViewed }) => {
  const messagesEndRef = useRef(null)
  const observerRef = useRef(null)
  const viewedMessagesRef = useRef(new Set())

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Mark messages as read when they become visible
  const markMessageAsViewed = useCallback(
    messageId => {
      if (!viewedMessagesRef.current.has(messageId)) {
        viewedMessagesRef.current.add(messageId)
        // Call the parent callback to handle marking as read
        if (onMessagesViewed) {
          onMessagesViewed([messageId])
        }
      }
    },
    [onMessagesViewed]
  )

  // Set up Intersection Observer to detect when messages become visible
  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const messageId = entry.target.dataset.messageId
            const isOwnMessage = entry.target.dataset.isOwn === 'true'

            // Only mark other people's messages as read (not our own messages)
            if (messageId && !isOwnMessage) {
              markMessageAsViewed(messageId)
            }
          }
        })
      },
      {
        threshold: 0.5, // Message is considered "viewed" when 50% visible
        rootMargin: '0px',
      }
    )

    // Observe all message elements
    const messageElements = document.querySelectorAll('[data-message-id]')
    messageElements.forEach(element => {
      observerRef.current.observe(element)
    })

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [messages, markMessageAsViewed])

  const formatTime = dateString => {
    const date = new Date(dateString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = dateString => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString()
    }
  }

  const shouldShowDate = (message, index) => {
    if (index === 0) return true

    const currentDate = new Date(message.attributes.created_at).toDateString()
    const previousDate = new Date(messages[index - 1].attributes.created_at).toDateString()

    return currentDate !== previousDate
  }

  if (loading && messages.length === 0) {
    return (
      <div className='chat-messages-loading'>
        <div className='chat-spinner'></div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className='chat-messages-empty'>
        <p>No messages yet. Start the conversation!</p>
      </div>
    )
  }

  return (
    <div className='chat-messages'>
      {messages.map((message, index) => {
        // Handle comparison for both regular users and law firm entities
        const senderId = String(message.attributes.sender.id)
        const currentUserId = String(currentUser.id)

        // Check if the message is from the current user
        const isOwn = senderId === currentUserId

        const isBlocked = message.attributes.blocked
        const isScanning = message.attributes._optimistic && !message.attributes.scanned

        return (
          <React.Fragment key={message.id}>
            {shouldShowDate(message, index) && (
              <div className='chat-date-divider'>
                <span>{formatDate(message.attributes.created_at)}</span>
              </div>
            )}

            <div
              className={`chat-message ${isOwn ? 'own' : 'other'}`}
              data-message-id={message.id}
              data-is-own={isOwn}
            >
              {!isOwn && (
                <div className='chat-message-avatar'>
                  {message.attributes.sender.display_name.charAt(0).toUpperCase()}
                </div>
              )}

              <div className='chat-message-content'>
                <div className={`chat-message-bubble ${isBlocked ? 'blocked' : ''}`}>
                  {isBlocked ? (
                    <p>{message.attributes.body}</p>
                  ) : (
                    <>
                      <p>{message.attributes.body}</p>
                      {isScanning && (
                        <span className='chat-message-status'>Scanning for Compliance...</span>
                      )}
                    </>
                  )}
                </div>

                {/* Display attachments - following Pilot's pattern */}
                {message.attributes.attachments && message.attributes.attachments.length > 0 && (
                  <div className='chat-message-attachments'>
                    {message.attributes.attachments.map((attachment, index) => (
                      <div key={index} className='chat-message-attachment'>
                        <a
                          href={attachment.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='chat-message-attachment-link'
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                          >
                            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                            <polyline points='7,10 12,15 17,10' />
                            <line x1='12' y1='15' x2='12' y2='3' />
                          </svg>
                          <span>{attachment.filename}</span>
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {isBlocked && (
                  <div className='chat-message-blocked'>
                    {message.attributes.violations && (
                      <div className='chat-message-violations'>
                        <div className='chat-message-blocked-header'>
                          <div className='chat-message-blocked-icon'>
                            <svg viewBox='0 0 24 24' fill='currentColor'>
                              <path d='M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zM11 7h2v6h-2V7zm0 8h2v2h-2v-2z' />
                            </svg>
                          </div>
                          <span>Message blocked due to compliance violations</span>
                        </div>
                        <ul>
                          {message.attributes.violations.map((violation, i) => (
                            <li key={i}>{violation.description}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className='chat-message-meta'>
                  <span className='chat-message-time'>
                    {formatTime(message.attributes.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      })}
      <div ref={messagesEndRef} />
    </div>
  )
}

export default ChatMessageList
